import {AdditionalParams} from '../hooks/paged-data-table/types';
import {UsersService} from '../api/generated/UsersService.ts';
import {useProduce} from '../hooks/use-produce.ts';
import {
  getActiveFiltersCount,
  PagedDataTableConfig,
  renderEditButton,
  usePagedDataTable,
} from '../hooks/paged-data-table/use-paged-data-table.tsx';
import {FC, useCallback} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faUnlock} from '@fortawesome/pro-regular-svg-icons';
import {Link} from 'react-router-dom';
import {Button, Group, Stack} from '@mantine/core';
import {ROUTES} from '../routes';
import {useNotification} from '../hooks/use-notifications.tsx';
import {useAsyncFn} from 'react-use';
import {PreventBubble} from '../components/prevent-bubble.tsx';
import {ConfirmButton} from '../components/confirm-button.tsx';
import {BasicPage} from '../components/basic-page.tsx';
import {AdvancedPagedRequest} from '../types';
import {UserSummaryDto} from '../api/generated/index.defs.ts';
import {z} from 'zod';
import {AllPermissions, Roles, RolesSchema} from '../api/generated/enums.ts';
import {createForm} from '../forms';
import {DefaultValues, SubmitHandler} from 'react-hook-form';
import {CopyButton} from '../components/copy-button.tsx';
import {useOptions} from '../hooks/use-options.ts';
import {PartnerSitesService} from '../api/generated/PartnerSitesService.ts';
import {mapNullToUndefined} from '../utils/zod.ts';

type FilterState = AdditionalParams<typeof UsersService.getAll>;
type ComponentState = {
  filter: FilterState;
  tableState?: Parameters<typeof UsersService.getAll>[0];
};

export const UserListing = () => {
  const [state, setState] = useProduce<ComponentState>({
    filter: {
      claims: [],
      roles: [],
    },
  });

  const additionalParams = state.filter;

  const onStateChange = useCallback(
    (tableState: AdvancedPagedRequest<UserSummaryDto>) => {
      setState((draft) => {
        draft.tableState = tableState;
      });
    },
    [setState],
  );

  const pagedDataTable = usePagedDataTable(UsersService.getAll, TableConfig, {
    additionalParams,
    onStateChange,
    renderFilter: (dismiss) => (
      <TableFilters
        defaultValues={state.filter as FilterSchema}
        onSubmit={(filters) => {
          setState((draft) => {
            draft.filter.partnerSiteId = filters.partnerSiteId;
            draft.filter.claims = filters.claims;
            draft.filter.roles = filters.roles;
          });

          dismiss();
        }}
      />
    ),
    filterBadge: getActiveFiltersCount(state.filter),
    renderActions: (
      <Button component={Link} to={ROUTES.ADMIN.USERS.CREATE.path}>
        <Group gap={'xs'}>
          <FontAwesomeIcon icon={faPlus} /> New User
        </Group>
      </Button>
    ),
  });

  return <BasicPage title='Users'>{pagedDataTable}</BasicPage>;
};

const filterSchema = z.object({
  partnerSiteId: mapNullToUndefined(z.number().optional()),
  roles: RolesSchema.array().optional(),
  claims: mapNullToUndefined(z.string().array().optional()),
}) satisfies z.Schema<FilterState>;
type FilterSchema = z.TypeOf<typeof filterSchema>;

const Form = createForm<FilterSchema>();

type TableFiltersProps = {
  defaultValues?: DefaultValues<FilterSchema>;
  onSubmit: SubmitHandler<FilterSchema>;
};
const TableFilters: FC<TableFiltersProps> = ({defaultValues, onSubmit}) => {
  const partnerSiteOptions =
    useOptions(PartnerSitesService.getOptions).value ?? [];
  return (
    <Form
      schema={filterSchema}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
    >
      <Stack>
        <Form.Select
          name='partnerSiteId'
          label='Partner Site'
          placeholder='Select Partner Site'
          data={partnerSiteOptions}
          searchable
          clearable
        />
        <Form.MultiSelect
          name={'roles'}
          label={'Roles'}
          enumObject={Roles}
          placeholder='Select Roles'
        />
        <Form.CheckboxGroup name={'claims'} label={'Claims'}>
          <Stack gap={'xs'}>
            {AllPermissions.map((permission, index) => (
              <Form.CheckboxGroup.Item
                value={permission}
                key={`claims-checkbox-${index}`}
                label={permission}
              />
            ))}
          </Stack>
        </Form.CheckboxGroup>
        <Form.Submit>Apply</Form.Submit>
      </Stack>
    </Form>
  );
};

export const ResetPasswordButton = ({id}) => {
  const notification = useNotification();

  const [sendResetState, sendReset] = useAsyncFn(async () => {
    const response = await UsersService.sendPasswordReset({
      id,
    });

    if (response.hasErrors) {
      notification.error('Failed to send password reset link.');
    } else {
      notification.success('Password reset link sent!');
    }
  });

  return (
    <PreventBubble>
      <ConfirmButton
        onConfirm={sendReset}
        loading={sendResetState.loading}
        icon={faUnlock}
        loadingText='Sending...'
        tooltip='Send Password Reset'
        variant='outline'
        styles={confirmButtonStyles}
      />
    </PreventBubble>
  );
};

const TableConfig = PagedDataTableConfig(UsersService.getAll, {
  columns: [
    {
      accessor: 'actions',
      title: '',
      render: (item) => (
        <Group>
          {renderEditButton({
            item: {...item, id: Number(item.id)},
            descriptor: 'User Account',
            route: ROUTES.ADMIN.USERS.DETAIL.path,
          })}
          <ResetPasswordButton id={item.id} />
        </Group>
      ),
      width: '100px',
    },
    {
      accessor: 'emailAddress',
      sortable: true,
      render: (item) => (
        <Group grow={false}>
          {item.emailAddress} <CopyButton value={item.emailAddress!} />
        </Group>
      ),
    },
    {
      accessor: 'name',
      render: (item) =>
        item.title && item.title !== ''
          ? `${item.title} ${item.firstName} ${item.lastName}`
          : `${item.firstName} ${item.lastName}`,
      sortable: true,
    },
    {
      accessor: 'role',
    },
    {
      accessor: 'inactive',
      title: 'Status',
      render: (item) => (item.inactive ? 'Inactive' : 'Active'),
    },
  ],
  defaultSort: {
    column: 'emailAddress',
    direction: 'ASC',
  },
  searchFieldNames: ['firstName', 'lastName', 'emailAddress'],
});

const confirmButtonStyles = {
  root: {
    padding: '0 1.125rem',
    border: 'none',
  },
};